// The playbooks rail's pure half: turning a Seamless note body into the plain
// text the rail reads and edits, which playbook was last open on a call, and
// the saver that never drops an edit. No DOM and no fetch here, so
// packages/core/test/playbooks.test.mjs runs it under node.

export interface Playbook {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly updated_at: string;
}

/** The Breeze assistant column beside the agenda (the chat, and the call's
 * Live panel) is off for now; the playbooks rail stands where it stood
 * (Adam, 2026-10-31). Turning this back on restores the old column. */
export const BREEZE_PANEL_ENABLED = false;

const TAG = /<\/?[a-z!][^>]*>/i;

const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ", "#39": "'" };

function decode(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+\d*|#39);/gi, (m, name: string) => {
    const lower = name.toLowerCase();
    if (lower in ENTITIES) return ENTITIES[lower]!;
    if (lower.startsWith("#x")) return String.fromCodePoint(parseInt(lower.slice(2), 16) || 32);
    if (lower.startsWith("#")) return String.fromCodePoint(parseInt(lower.slice(1), 10) || 32);
    return m;
  });
}

/** A note body as plain text with newlines: HTML is stripped to its text (a
 * list item keeps a "- "), anything else is taken as it is. */
export function bodyToText(body: string): string {
  let s = (body ?? "").replace(/\r\n?/g, "\n");
  if (TAG.test(s)) {
    s = s
      .replace(/\n/g, " ")
      .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, "")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<li[^>]*>/gi, "\n- ")
      .replace(/<\/(p|div|h[1-6]|ul|ol|blockquote|pre|tr|table)>/gi, "\n\n")
      .replace(/<\/li>/gi, "")
      .replace(/<[^>]+>/g, "");
    s = decode(s)
      .split("\n")
      .map((l) => l.replace(/[ \t]+/g, " ").trim())
      .join("\n");
  }
  return s.replace(/\n{3,}/g, "\n\n").trim();
}

/** What an editable element's text is saved as: plain text, Unix newlines,
 * no trailing blank lines, non-breaking spaces as spaces. */
export function textForSave(text: string): string {
  return text.replace(/\r\n?/g, "\n").replace(/ /g, " ").replace(/\n{3,}/g, "\n\n").replace(/\s+$/, "");
}

// ---- reading it formatted ------------------------------------------------------------

export interface Span {
  readonly text: string;
  readonly bold: boolean;
  readonly italic: boolean;
}

export type Block =
  | { readonly kind: "heading"; readonly level: 1 | 2 | 3; readonly spans: Span[] }
  | { readonly kind: "para"; readonly spans: Span[] }
  | { readonly kind: "item"; readonly marker: string; readonly depth: 0 | 1; readonly spans: Span[] }
  | { readonly kind: "gap" };

/** **bold** and *italic* as styled runs. A marker with no partner stays as the
 * typed characters, and a space before a closing ** ("**Why? **") is allowed. */
export function inlineSpans(line: string): Span[] {
  const out: Span[] = [];
  let bold = false;
  let italic = false;
  let buf = "";
  const flush = (): void => {
    if (buf) out.push({ text: buf, bold, italic });
    buf = "";
  };
  for (let i = 0; i < line.length; i++) {
    const c = line[i]!;
    if (c === "*" && line[i + 1] === "*") {
      if (bold || line.indexOf("**", i + 2) !== -1) {
        flush();
        bold = !bold;
        i++;
        continue;
      }
    } else if (c === "*") {
      const next = line[i + 1] ?? "";
      const prev = line[i - 1] ?? "";
      if (italic ? !/\s/.test(prev) : next !== "" && !/\s/.test(next) && /(^|[^*])\*(?!\*)/.test(line.slice(i + 1))) {
        flush();
        italic = !italic;
        continue;
      }
    }
    buf += c;
  }
  flush();
  if (out.length) {
    out[0] = { ...out[0]!, text: out[0]!.text.replace(/^\s+/, "") };
    const last = out.length - 1;
    out[last] = { ...out[last]!, text: out[last]!.text.replace(/\s+$/, "") };
  }
  return out.filter((s) => s.text !== "");
}

const ITEM = /^(\s*)([-*•]|\d+[a-z]?[.)]|[a-z][.)])\s+(\S.*)$/;

/** The rail's text as blocks to draw: a # line or a line that is all bold is a
 * heading, "- ", "1." and "1a." lines are items, a blank line is a gap, and
 * every other line is its own paragraph (a script is read line by line). */
export function parseBody(text: string, opts: { firstIsTitle?: boolean } = {}): Block[] {
  const blocks: Block[] = [];
  for (const raw of (text ?? "").replace(/\r\n?/g, "\n").split("\n")) {
    if (raw.trim() === "") {
      if (blocks.length && blocks[blocks.length - 1]!.kind !== "gap") blocks.push({ kind: "gap" });
      continue;
    }
    const h = /^\s{0,3}(#{1,3})\s+(.*)$/.exec(raw);
    if (h) {
      blocks.push({ kind: "heading", level: h[1]!.length as 1 | 2 | 3, spans: inlineSpans(h[2]!).map((s) => ({ ...s, bold: true })) });
      continue;
    }
    const it = ITEM.exec(raw);
    if (it && !/^\*\*/.test(raw.trim())) {
      const marker = /^[-*•]$/.test(it[2]!) ? "•" : it[2]!;
      blocks.push({ kind: "item", marker, depth: it[1]!.length >= 2 ? 1 : 0, spans: inlineSpans(it[3]!) });
      continue;
    }
    const spans = inlineSpans(raw);
    if (spans.length && spans.every((s) => s.bold)) blocks.push({ kind: "heading", level: 2, spans });
    else blocks.push({ kind: "para", spans });
  }
  while (blocks.length && blocks[blocks.length - 1]!.kind === "gap") blocks.pop();
  // A note's first line is its title (Seamless names it that way), so it is the
  // heading whether or not it was typed bold.
  const first = blocks[0];
  if (opts.firstIsTitle && first && first.kind === "para") {
    blocks[0] = { kind: "heading", level: 2, spans: first.spans.map((s) => ({ ...s, bold: true })) };
  }
  return blocks;
}

// ---- which playbook was open, per call ------------------------------------------

interface KeyStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function openKey(callSlug: string | null): string {
  return `breeze:playbook-open:${callSlug || "_"}`;
}

export function readOpen(store: KeyStore | undefined, callSlug: string | null): string | null {
  try {
    return store?.getItem(openKey(callSlug)) || null;
  } catch {
    return null;
  }
}

export function writeOpen(store: KeyStore | undefined, callSlug: string | null, id: string | null): void {
  try {
    if (id) store?.setItem(openKey(callSlug), id);
    else store?.removeItem(openKey(callSlug));
  } catch {
    // Not remembered.
  }
}

// ---- the saver --------------------------------------------------------------------

export type SaveStatus = "idle" | "saving" | "saved" | "failed";

export interface SaverDeps {
  readonly save: (id: string, body: string, opts: { keepalive?: boolean }) => Promise<{ updated_at?: string }>;
  /** Called after every change of status or of a pending body. */
  readonly onChange?: () => void;
  /** A copy of an unsaved body that outlives the page (null clears it). */
  readonly stash?: (id: string, body: string | null) => void;
  readonly setTimer?: (fn: () => void, ms: number) => unknown;
  readonly clearTimer?: (handle: unknown) => void;
}

export const RETRY_MS = [1000, 2000, 4000, 8000, 15000] as const;

/** Holds each playbook's unsaved text until Seamless has it. A failed save
 * keeps the text and tries again, longer each time; an edit made while a save
 * is out is not cleared by that save finishing. */
export class PlaybookSaver {
  status: SaveStatus = "idle";
  private readonly deps: SaverDeps;
  private readonly pending = new Map<string, { body: string; version: number }>();
  private readonly saved = new Map<string, string>();
  private version = 0;
  private running: Promise<void> | null = null;
  private attempt = 0;
  private timer: unknown = null;

  constructor(deps: SaverDeps) {
    this.deps = deps;
  }

  /** The unsaved text for a playbook, if there is any. */
  pendingBody(id: string): string | undefined {
    return this.pending.get(id)?.body;
  }

  hasPending(): boolean {
    return this.pending.size > 0;
  }

  /** The text last confirmed saved this session, which a stale list must not undo. */
  savedBody(id: string): string | undefined {
    return this.saved.get(id);
  }

  /** Records the playbook's text; `base` is what Seamless last had. */
  edit(id: string, body: string, base: string): void {
    const text = textForSave(body);
    if (text === textForSave(base) && !this.pending.has(id)) return;
    this.pending.set(id, { body: text, version: ++this.version });
    this.deps.stash?.(id, text);
    this.deps.onChange?.();
  }

  /** Sends everything unsaved now, one at a time. */
  flush(opts: { keepalive?: boolean } = {}): Promise<void> {
    if (opts.keepalive) {
      // The page is going away: send every body at once and do not wait.
      for (const [id, p] of this.pending) void this.deps.save(id, p.body, opts).catch(() => {});
      return Promise.resolve();
    }
    if (this.running) return this.running.then(() => (this.pending.size ? this.flush() : undefined));
    if (this.timer !== null) {
      (this.deps.clearTimer ?? clearTimeout)(this.timer as ReturnType<typeof setTimeout>);
      this.timer = null;
    }
    if (this.pending.size === 0) return Promise.resolve();
    this.running = this.run().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  private setStatus(s: SaveStatus): void {
    this.status = s;
    this.deps.onChange?.();
  }

  private async run(): Promise<void> {
    this.setStatus("saving");
    for (const [id, p] of [...this.pending]) {
      try {
        await this.deps.save(id, p.body, {});
      } catch {
        this.attempt = Math.min(this.attempt + 1, RETRY_MS.length);
        this.setStatus("failed");
        const wait = RETRY_MS[this.attempt - 1]!;
        this.timer = (this.deps.setTimer ?? setTimeout)(() => {
          this.timer = null;
          void this.flush();
        }, wait);
        return;
      }
      this.saved.set(id, p.body);
      const now = this.pending.get(id);
      if (now && now.version === p.version) {
        this.pending.delete(id);
        this.deps.stash?.(id, null);
      }
    }
    this.attempt = 0;
    if (this.pending.size) return this.run();
    this.setStatus("saved");
  }
}

/** Seamless's list with this session's unsaved and just-saved text laid over
 * it, so a refresh that raced a save never shows the old words. */
export function overlay(list: readonly Playbook[], saver: Pick<PlaybookSaver, "pendingBody" | "savedBody">): Playbook[] {
  return list.map((p) => {
    const mine = saver.pendingBody(p.id) ?? saver.savedBody(p.id);
    return mine === undefined || mine === p.body ? p : { ...p, body: mine };
  });
}
